import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ColumnFilterRow, applyColumnFilters } from "../../../Components/Common/DashboardKit";
import useTableSort from "../../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../../Components/Common/DashboardKit/exportCsv";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Button, Badge, Table,
} from 'reactstrap';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DashboardPageHeader, TableSkeleton } from '../../../Components/Common/DashboardKit';
import api from '../../../api';
// Round DMS-UAT-1 (2026) — Issue 2: rows now open the same DmsViewModal the
// rest of DMS uses. The modal already carries the R4/R5 Approve / Needs
// Revision action bar (gated by the caller's pending DmsApproval row), so
// the approver can review every tab and act without leaving the page.
import DmsViewModal from '../ECN/DmsViewModal';
// Round DMS-UAT-1 — Issue 3: sticky Doc-ID (left) + Action (right) columns.
import '../../../assets/styles/dmsTables.css';

/**
 * My Pending Approvals (any authenticated user)
 *
 * Surfaces documents (Status = 0, Pending Approval) where the current user
 * is in the approver list with Approved = NULL.
 *
 * Backend:
 *   GET /api/dms/my-pending-approvals?empId=N
 *     (Round DMS-UAT-1: caller-identity enforced server-side; the payload
 *      now includes authorName + status label for display/export.)
 *
 * Round DMS-UAT-1 (2026) — UAT fixes on this page:
 *   • Issue 2 — the old Review button linked to /dms/document/:id, a route
 *     that never existed, so "none of the items open". Rows now open the
 *     DmsViewModal (with in-app Approve / Needs Revision) and the list
 *     refreshes when the modal closes so acted-on rows disappear.
 *   • Issue 3 — Doc ID pinned left, Action pinned right, titles wrap.
 *   • Issue 4/5 — Export uses friendly labeled columns (Author NAME,
 *     separate Author EmpID, Status, ISO date Excel can format) instead of
 *     dumping raw object keys; no Manager column.
 */

const docTypeLabel = (t) =>
    ({ 1: 'SOP', 2: 'WI', 3: 'FCD', 4: 'ECN', 5: 'LIT' })[t] || `Type ${t}`;

// Excel-friendly timestamp: "YYYY-MM-DD HH:MM" is recognized as a date by
// Excel/Sheets (Issue 4: raw ISO-with-T and locale strings were not
// convertible via Format Cells → Date).
const excelDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const EXPORT_COLUMNS = [
    { label: "Document",     get: (r) => r.docId || "" },
    { label: "Type",         get: (r) => docTypeLabel(r.docType) },
    { label: "Title",        get: (r) => r.docTitle || "" },
    { label: "Revision",     get: (r) => r.rev ?? "" },
    { label: "Author",       get: (r) => r.authorName || "" },
    { label: "Author EmpID", get: (r) => r.author ?? "" },
    { label: "Status",       get: (r) => r.status || "Pending Approval" },
    { label: "Created Date", get: (r) => excelDate(r.createdTime) },
];

const MyPendingApprovals = () => {
    const authUser = useMemo(() => {
        try { return JSON.parse(sessionStorage.getItem('authUser') || '{}'); }
        catch { return {}; }
    }, []);
    const empId = Number(authUser.uid);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch] = useState('');
    // Round DMS-UAT-1 — Issue 2: modal preview state.
    const [previewDmsId, setPreviewDmsId] = useState(null);
    // Round 34 — EPO-style per-column filters + click-to-sort (the global
    // search box is replaced by the filter row).
    const [columnFilters, setColumnFilters] = useState({});
    const tblColumns = React.useMemo(() => [
        { id: "docId",    label: "Doc ID", type: "text", get: (r) => r.docId || "" },
        { id: "type",     label: "Type",   type: "text", get: (r) => docTypeLabel(r.docType) },
        { id: "docTitle", label: "Title",  type: "text", get: (r) => r.docTitle || "" },
        { id: "rev",      label: "Rev",    type: "text", get: (r) => String(r.rev ?? "") },
        // Round DMS-UAT-1 — Issue 5: filter/sort on the author's NAME.
        { id: "author",   label: "Author", type: "text", get: (r) => r.authorName || String(r.author ?? "") },
        { id: "createdTime", label: "Submitted", type: "date", get: (r) => r.createdTime || "" },
        { id: "act",      label: "Action", type: "none" },
    ], []);

    const loadAll = useCallback(async () => {
        if (!empId) { setLoading(false); setLoadError('No user in session.'); return; }
        setLoading(true);
        setLoadError(null);
        try {
            const res = await api.get('dms/my-pending-approvals', { params: { empId } });
            setRows(res.data || []);
        } catch (err) {
            setLoadError(err?.response?.data?.error || err?.message || 'Failed to load.');
        } finally {
            setLoading(false);
        }
    }, [empId]);

    useEffect(() => { loadAll(); }, [loadAll]);

    // Round DMS-UAT-1 — Issue 2: when the preview modal closes, refresh the
    // list. If the approver acted inside the modal (Approve / Needs
    // Revision) the row is no longer pending and disappears here.
    const closePreview = useCallback(() => {
        setPreviewDmsId(null);
        loadAll();
    }, [loadAll]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) =>
            [r.docId, r.docTitle, r.authorName, docTypeLabel(r.docType)]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
                .includes(q)
        );
    }, [rows, search]);
    const filteredBase = applyColumnFilters(filtered, tblColumns, columnFilters);
    const { sorted: view, sort, toggleSort } = useTableSort(filteredBase, tblColumns);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-inbox-line"
                    title="My Pending Approvals"
                    subtitle="Documents awaiting your approval"
                    tone="violet"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "DMS", to: "/workflows?wf=dms" },
                        { label: "My Pending Approvals" },
                    ]}
                />

                <Row>
                    <Col xs={12}>
                        <Card>
                            <CardHeader className="d-flex flex-wrap gap-2 align-items-center">
                                <div className="flex-grow-1">
                                    <h5 className="card-title mb-0">
                                        <i className="ri-mail-check-line align-bottom me-1 text-primary"></i>
                                        Documents pending your action
                                    </h5>
                                    <div className="text-muted small mt-1">
                                        Click <strong>Review</strong> to open a document, check every tab,
                                        and Approve or request revision — or use the link in the email notification.
                                    </div>
                                </div>
                                {/* Round 34 — search box replaced by the per-column filter row */}
                                <Button color="light" onClick={loadAll} disabled={loading} title="Reload">
                                    <i className="ri-refresh-line align-bottom"></i>
                                </Button>
                            </CardHeader>

                            <CardBody>
                                {loading && (
                                    <div className="table-responsive">
                                        <Table className="align-middle table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Doc ID</th><th>Type</th><th>Title</th><th>Rev</th>
                                                    <th>Author</th><th>Submitted</th><th className="text-end">Action</th>
                                                </tr>
                                                <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                                            </thead>
                                            <tbody>
                                                <TableSkeleton rows={6} columns={7} />
                                            </tbody>
                                        </Table>
                                    </div>
                                )}

                                {!loading && loadError && (
                                    <div className="alert alert-danger d-flex align-items-center">
                                        <i className="ri-error-warning-line me-2 fs-5"></i>
                                        <div className="flex-grow-1">{loadError}</div>
                                        <Button size="sm" color="danger" outline onClick={loadAll}>Retry</Button>
                                    </div>
                                )}

                                {!loading && !loadError && filtered.length === 0 && (
                                    <div className="text-center py-5 text-muted">
                                        <i className="ri-emotion-happy-line" style={{ fontSize: 36 }}></i>
                                        <div className="mt-2">
                                            {search ? 'No matches.' : 'No approvals pending — you\'re all caught up.'}
                                        </div>
                                    </div>
                                )}

                                {!loading && !loadError && filtered.length > 0 && (
                            <>
                            {/* Round DMS-UAT-1 — Issue 4: friendly labeled
                                columns (names + Status + Excel-parseable
                                dates) exported for the CURRENT FILTERED set,
                                not raw object keys. */}
                            <div className="d-flex justify-content-end mb-2">
                                <button
                                    type="button"
                                    className="btn btn-soft-primary btn-sm"
                                    title="Exports the rows currently shown (all filters applied)"
                                    onClick={() => exportRowsToCsv("dms-my-pending-approvals.csv", EXPORT_COLUMNS, view)}
                                >
                                    <i className="ri-download-2-line align-bottom me-1" />Export CSV ({view.length})
                                </button>
                            </div>
<div className="table-responsive">
                                        <Table className="align-middle mb-0 dms-sticky-table" hover>
                                            <thead className="table-light">
                                                <tr>
                                                    {tblColumns.slice(0, 6).map((c) => (
                                                        <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                                                    ))}
                                                    <th className="text-end">Action</th>
                                                </tr>
                                                {/* Round DMS-UAT-2 — per-column filter row on the
                                                    loaded table, matching Awaiting Release. */}
                                                <ColumnFilterRow columns={tblColumns} filters={columnFilters} onChange={setColumnFilters} />
                                            </thead>
                                            <tbody>
                                                {view.map((r) => (
                                                    <tr
                                                        key={r.dmsId}
                                                        style={{ cursor: "pointer" }}
                                                        title="Open document for review"
                                                        onClick={() => setPreviewDmsId(r.dmsId)}
                                                    >
                                                        <td><code>{r.docId}</code></td>
                                                        <td><Badge color="info" pill>{docTypeLabel(r.docType)}</Badge></td>
                                                        <td className="dms-title-cell" title={r.docTitle || ""}>
                                                            {r.docTitle || <span className="text-muted">—</span>}
                                                        </td>
                                                        <td>{r.rev ?? '—'}</td>
                                                        <td>
                                                            {r.authorName
                                                                ? (<>
                                                                    {r.authorName}
                                                                    <div className="text-muted small">ID: {r.author}</div>
                                                                  </>)
                                                                : (r.author ?? <span className="text-muted">—</span>)}
                                                        </td>
                                                        <td>{r.createdTime ? new Date(r.createdTime).toLocaleString() : <span className="text-muted">—</span>}</td>
                                                        <td className="text-end">
                                                            <Button
                                                                color="primary"
                                                                size="sm"
                                                                outline
                                                                onClick={(e) => { e.stopPropagation(); setPreviewDmsId(r.dmsId); }}
                                                                title="Open this document for review and approval"
                                                            >
                                                                <i className="ri-eye-line align-bottom me-1"></i>Review
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                            </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Round DMS-UAT-1 — Issue 2: the document preview + in-app
                Approve / Needs Revision. Same modal as the main list and
                Awaiting Release, so behavior and server-side authorization
                are identical everywhere. */}
            {previewDmsId && (
                <DmsViewModal
                    isOpen={!!previewDmsId}
                    toggle={closePreview}
                    dmsId={previewDmsId}
                />
            )}

            <ToastContainer closeButton={false} limit={3} />
        </div>
    );
};

export default MyPendingApprovals;
